import {
  Children,
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState
} from "react"
import { IconDefinition } from "@fortawesome/fontawesome-svg-core"
import { faChevronDown, faList, faUserFriends } from "@fortawesome/free-solid-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import PopulationChip from "@/entities/population/ui/PopulationChip"
import { Population } from "@/entities/population/model/population.types"
import RiskFamilyChip from "@/entities/risk-family/ui/RiskFamilyChip"
import { RiskFamily } from "@/entities/risk-family/model/risk-family.types"
import SiteChip from "@/entities/site/ui/SiteChip"
import SiteFilterChips from "@/entities/site/ui/SiteFilterChips"
import { Site } from "@/entities/site/model/site.types"
import { risksSitesNavItem } from "@/nav/nav.app"

/**
 * Accordéon : un seul FilterBlock ouvert à la fois. Le groupe est fourni par FilterSidePanel,
 * seul point d'entrée des blocks : sans lui, un block s'affiche mais ne s'ouvre pas.
 */
type FilterBlockGroup = {
  openId: string | null
  registerBlock: (id: string) => () => void
  setOpenId: (id: string | null) => void
}

const FilterBlockGroupContext = createContext<FilterBlockGroup | null>(null)

export function FilterBlockGroupProvider({ children }: { children: ReactNode }) {
  const [openId, setOpenId] = useState<string | null>(null)
  //Les blocks se declarent eux-memes : ils se masquent quand leur liste est vide, donc le panel
  //ne connait pas a l'avance le nombre reellement affiche.
  const [blockIds, setBlockIds] = useState<string[]>([])
  const hasAutoOpenedRef = useRef(false)
  // Verrou pendant la transition : quand un block s'ouvre/ferme, les autres se déplacent et un
  // header peut "balayer" sous le curseur -> mouseEnter parasite -> clignotement. On ignore les
  // changements demandés pendant ~320ms (durée de l'anim) pour absorber ces déclenchements.
  const lockRef = useRef(false)
  const requestOpen = useCallback((id: string | null) => {
    if (lockRef.current) return
    lockRef.current = true
    setOpenId(id)
    setTimeout(() => {
      lockRef.current = false
    }, 320)
  }, [])
  const registerBlock = useCallback((id: string) => {
    setBlockIds((ids) => (ids.includes(id) ? ids : ids.concat(id)))
    return () => setBlockIds((ids) => ids.filter((item) => item !== id))
  }, [])

  //Un seul block affiche : il n'y a rien a choisir, on l'ouvre d'office. Une seule fois par panel,
  //sinon un block referme par l'utilisateur se rouvrirait aussitot. setOpenId direct pour ne pas
  //consommer le verrou d'animation de requestOpen.
  useEffect(() => {
    if (hasAutoOpenedRef.current || blockIds.length !== 1) return

    hasAutoOpenedRef.current = true
    setOpenId(blockIds[0])
  }, [blockIds])

  const value = useMemo(() => ({ openId, registerBlock, setOpenId: requestOpen }), [openId, registerBlock, requestOpen])
  return <FilterBlockGroupContext.Provider value={value}>{children}</FilterBlockGroupContext.Provider>
}

interface Props {
  children: ReactNode
  icon: IconDefinition
  label: string
  selectedContent?: ReactNode
}

export default function FilterBlock({ children, icon, label, selectedContent }: Props) {
  const id = useId()
  const group = useContext(FilterBlockGroupContext)
  const registerBlock = group?.registerBlock

  useEffect(() => registerBlock?.(id), [id, registerBlock])

  const isOpen = group?.openId === id

  const open = () => group?.setOpenId(id)
  const toggle = () => group?.setOpenId(isOpen ? null : id)

  const hasSelectedContent = Children.count(selectedContent) > 0
  // Block fermé avec une sélection : les chips remplacent "icône + Filtrer par XXX".
  const showChipsSummary = !isOpen && hasSelectedContent
  const scrollAreaStyle = { scrollbarGutter: "stable" } as React.CSSProperties

  return (
    <section
      className={[
        "group mx-[30px] mb-6 flex flex-col overflow-hidden rounded-[12px] border border-slate-200 bg-white shadow-sm",
        // On anime flex-grow (0->1) pour ouvrir/fermer en douceur tout en remplissant le panel.
        "transition-[flex-grow,background-color,box-shadow] duration-300 ease-out",
        // Ouvert : blanc + ombre (pas de survol gris, sinon grande surface grise qui "flashe").
        // Fermé : survol gris comme affordance de clic.
        isOpen ? "shadow-md" : "hover:bg-slate-50 hover:shadow-md",
        // Ouvert : min-h-0 + grow => remplit et peut se comprimer (scroll interne).
        // Fermé : shrink-0 + grow-0 => reste figé à la hauteur du header (jamais écrasé).
        isOpen ? "min-h-0 grow" : "shrink-0 grow-0"
      ].join(" ")}
      onMouseEnter={open}>
      <div
        role="button"
        tabIndex={0}
        className="box-border flex min-h-[62px] w-full shrink-0 items-center gap-3 px-8 py-3 text-left"
        onKeyDown={(event) => {
          if (event.key === "Enter" || event.key === " ") {
            event.preventDefault()
            toggle()
          }
        }}
        onClick={toggle}>
        <FontAwesomeIcon icon={icon} fixedWidth className="shrink-0 text-[16px] grey-t" />

        <div className="min-w-0 flex-1">
          <span className="text-base font-normal text-slate-700">{label}</span>
        </div>

        <FontAwesomeIcon
          icon={faChevronDown}
          fixedWidth
          className={`_hover shrink-0 text-[16px] grey-t transition-transform duration-300 ${isOpen ? "rotate-180" : ""}`}
        />
      </div>

      {/* Fermé avec une sélection : on garde le header et on montre les chips sur une 2e ligne. */}
      {showChipsSummary && (
        <div className="box-border flex shrink-0 flex-wrap gap-[10px] px-8 pb-8">{selectedContent}</div>
      )}

      {/* Ouvert : flex-1 remplit la hauteur révélée par le flex-grow de la section.
          Fermé : grid-rows-[0fr] collapse VRAIMENT le contenu à 0 (sinon la carte fermée
          garderait la hauteur naturelle du contenu caché). */}
      <div
        className={`grid min-h-0 flex-1 transition-[grid-template-rows,opacity] duration-300 ease-out ${
          isOpen ? "grid-rows-[1fr] opacity-100" : "grid-rows-[0fr] opacity-0"
        }`}>
        <div className="min-h-0 overflow-hidden">
          <div className="box-border h-full overflow-y-auto px-8 pb-6 pt-0" style={scrollAreaStyle}>
            {children}
          </div>
        </div>
      </div>
    </section>
  )
}

interface SiteFilterBlockProps {
  onRemoveSite: (siteId: string) => void
  onReplace: () => void
  selectedSites: Site[]
  showAdd: boolean
  showRemove: boolean
  sites: Site[]
}

export function SiteFilterBlock({
  onRemoveSite,
  onReplace,
  selectedSites,
  showAdd,
  showRemove,
  sites
}: SiteFilterBlockProps) {
  if (sites.length === 0) return null

  return (
    <FilterBlock
      icon={risksSitesNavItem.icon}
      label="Filtrer par lieu"
      selectedContent={selectedSites.map((site) => (
        <SiteChip key={site.id} site={site} onRemoveClick={showRemove ? () => onRemoveSite(site.id) : undefined} />
      ))}>
      <SiteFilterChips
        sites={sites}
        selectionMode="single"
        showAdd={showAdd}
        showRemove={showRemove}
        onReplace={onReplace}
      />
    </FilterBlock>
  )
}

interface PopulationFilterBlockProps {
  onAddPopulation: (populationId: string) => void
  onRemovePopulation: (populationId: string) => void
  onReplacePopulation: (populationId: string) => void
  populations: Population[]
  selectedPopulationIds: string[]
  selectedPopulations: Population[]
  showAdd: boolean
  showRemove: boolean
}

export function PopulationFilterBlock({
  onAddPopulation,
  onRemovePopulation,
  onReplacePopulation,
  populations,
  selectedPopulationIds,
  selectedPopulations,
  showAdd,
  showRemove
}: PopulationFilterBlockProps) {
  if (populations.length === 0) return null

  return (
    <FilterBlock
      icon={faUserFriends}
      label="Filtrer par fonction"
      selectedContent={selectedPopulations.map((population) => (
        <PopulationChip
          key={population.id}
          population={population}
          onClick={() => onReplacePopulation(population.id)}
          onRemoveClick={showRemove ? () => onRemovePopulation(population.id) : undefined}
        />
      ))}>
      <div className="mt-10 flex flex-wrap gap-[10px]">
        {populations.map((population) => (
          <PopulationChip
            key={population.id}
            population={population}
            onAddClick={
              showAdd && !selectedPopulationIds.includes(population.id)
                ? () => onAddPopulation(population.id)
                : undefined
            }
            onClick={() => onReplacePopulation(population.id)}
            onRemoveClick={showRemove ? () => onRemovePopulation(population.id) : undefined}
          />
        ))}
      </div>
    </FilterBlock>
  )
}

interface RiskFamilyFilterBlockProps {
  families: RiskFamily[]
  familyRiskIdsById: Map<string, string[]>
  onReplace: () => void
  selectedFamiliesSummary: RiskFamily[]
  showAdd: boolean
  showRemove: boolean
}

export function RiskFamilyFilterBlock({
  families,
  familyRiskIdsById,
  onReplace,
  selectedFamiliesSummary,
  showAdd,
  showRemove
}: RiskFamilyFilterBlockProps) {
  if (families.length === 0) return null

  return (
    <FilterBlock
      icon={faList}
      label="Filtrer par famille de risques"
      selectedContent={selectedFamiliesSummary.map((family) => (
        <RiskFamilyChip
          key={family.id}
          family={family}
          onReplace={onReplace}
          showRemove={showRemove}
          riskIds={familyRiskIdsById.get(family.id) ?? []}
        />
      ))}>
      <div className="mt-10 flex flex-wrap gap-[10px]">
        {families.map((family) => (
          <RiskFamilyChip
            key={family.id}
            family={family}
            onReplace={onReplace}
            showAdd={showAdd}
            showRemove={showRemove}
            riskIds={familyRiskIdsById.get(family.id) ?? []}
          />
        ))}
      </div>
    </FilterBlock>
  )
}
